import {
  AlertCircle,
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Crown,
  Settings,
  Star,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { billingAPI } from "../../services/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Progress } from "../ui/progress";

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [usage, setUsage] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if coming from payment success (via location state or search params)
    const fromPayment =
      location.state?.fromPayment ||
      new URLSearchParams(location.search).get("fromPayment") === "true";

    if (fromPayment) {
      // Clear the state first
      navigate(location.pathname, { replace: true, state: {} });

      // Load data immediately, then retry a few times if needed
      let retryCount = 0;
      const maxRetries = 3;

      const loadWithRetry = async (isRetry = false) => {
        try {
          if (!isRetry) {
            setLoading(true);
          }

          // Load fresh data
          const [usageResponse, currentPlanResponse] = await Promise.all([
            billingAPI.getUsage(),
            billingAPI.getCurrentPlan(),
          ]);

          const newPlanName = currentPlanResponse.data?.planName;

          // Always update state
          setUsage(usageResponse.data);
          setCurrentPlan(currentPlanResponse.data);
          setLoading(false);

          // If we have a non-free plan, show success
          if (newPlanName && newPlanName !== "free") {
            toast({
              title: "✅ Plan Updated!",
              description: `Your plan has been updated to ${newPlanName}`,
            });
            return; // Success, stop retrying
          }

          // If plan is still free and we haven't retried enough, retry
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(() => loadWithRetry(true), 2000);
          } else {
            // Max retries reached
            toast({
              title: "⚠️ Plan Update Pending",
              description:
                "Your plan update is being processed. Please refresh the page in a few moments.",
              variant: "default",
            });
          }
        } catch (error) {
          console.error("Error loading settings with retry:", error);
          setLoading(false);
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(() => loadWithRetry(true), 2000);
          } else {
            toast({
              title: "Error",
              description: "Failed to load settings. Please refresh the page.",
              variant: "destructive",
            });
          }
        }
      };

      // Start loading immediately
      loadWithRetry();
    } else {
      loadSettingsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data when page becomes visible (user returns from payment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Wait a bit for webhook to process
        setTimeout(() => {
          loadSettingsData(false); // Don't show loading spinner on refresh
        }, 1000);
      }
    };

    // Also refresh on focus (when user comes back to tab)
    const handleFocus = () => {
      setTimeout(() => {
        loadSettingsData(false); // Don't show loading spinner on refresh
      }, 1000);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettingsData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const [usageResponse, currentPlanResponse] = await Promise.all([
        billingAPI.getUsage(),
        billingAPI.getCurrentPlan(),
      ]);

      console.log("Settings data loaded:", {
        usage: usageResponse.data,
        currentPlan: currentPlanResponse.data,
      });

      setUsage(usageResponse.data);
      setCurrentPlan(currentPlanResponse.data);
    } catch (error) {
      console.error("Failed to load settings data:", error);
      toast({
        title: "Error loading settings",
        description: "Failed to load settings information",
        variant: "destructive",
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case "basic":
        return <Zap className="h-6 w-6 text-blue-500" />;
      case "growth":
        return <Star className="h-6 w-6 text-purple-500" />;
      case "premium":
        return <Star className="h-6 w-6 text-purple-500" />;
      case "enterprise":
        return <Crown className="h-6 w-6 text-emerald-500" />;
      default:
        return <CreditCard className="h-6 w-6 text-gray-500" />;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case "basic":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
      case "growth":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300";
      case "premium":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300";
      case "enterprise":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "past_due":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "canceled":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Settings
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your plan and service usage
            </p>
          </div>
          <Button
            onClick={() => navigate("/billing")}
            className="bg-primary hover:bg-primary/90"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Manage Plan
          </Button>
        </div>

        {/* Current Plan Card */}
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getPlanIcon(currentPlan?.planName)}
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Plan Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold capitalize">
                    {currentPlan?.planName === "free"
                      ? "Free"
                      : currentPlan?.planName}
                  </h3>
                  <Badge className={getPlanColor(currentPlan?.planName)}>
                    {currentPlan?.planStatus === "active"
                      ? "Active"
                      : "Inactive"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(currentPlan?.planStatus)}
                  <span className="text-muted-foreground">
                    Status:{" "}
                    {currentPlan?.planStatus === "active"
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="space-y-3">
                {/* Purchase Date - تاریخ خرید */}
                {(currentPlan?.subscriptionStartDate ||
                  currentPlan?.subscriptionInfo?.currentPeriodStart) && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground">
                        Purchase Date
                      </span>
                      <span className="font-medium">
                        {new Date(
                          currentPlan?.subscriptionStartDate ||
                            currentPlan?.subscriptionInfo?.currentPeriodStart
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Days Remaining - روزهای باقی‌مانده */}
                {usage?.planName &&
                  usage.planName !== "free" &&
                  usage?.daysRemaining !== undefined &&
                  usage?.daysRemaining !== null &&
                  usage.daysRemaining > 0 && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          Days Remaining
                        </span>
                        <span className="font-bold text-lg">
                          {usage.daysRemaining} days
                        </span>
                      </div>
                    </div>
                  )}

                {currentPlan?.subscriptionInfo && (
                  <>
                    {currentPlan.subscriptionEndDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            Expires
                          </span>
                          <span className="font-medium">
                            {new Date(
                              currentPlan.subscriptionEndDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          Price
                        </span>
                        <span className="font-medium">
                          £
                          {(
                            currentPlan.subscriptionInfo.price.amount / 100
                          ).toFixed(0)}
                          /{currentPlan.subscriptionInfo.price.interval}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage Statistics */}
        {usage && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Credit Usage
              </CardTitle>
              <CardDescription>
                Your monthly verification usage and limits
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {usage?.checksIncluded ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Allowed per month
                  </div>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {usage?.checksUsed ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">Used</div>
                </div>
                <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                    {usage?.remainingChecks ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Remaining
                  </div>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {usage?.paygUsed ?? 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Additional usage
                  </div>
                </div>
              </div>

              {/* Credit Consumption Summary */}
              {usage?.checksIncluded > 0 && (
                <>
                  <div className="mb-6 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Credit Consumption</span>
                      <span className="font-bold text-lg">
                        {usage?.checksUsed || 0} / {usage?.checksIncluded || 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>
                        {usage?.checksIncluded
                          ? `${Math.round(
                              ((usage?.checksUsed || 0) / usage.checksIncluded) *
                                100
                            )}% consumed`
                          : "No limit set"}
                      </span>
                      <span>{usage?.remainingChecks || 0} credits remaining</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Usage Progress</span>
                      <span className="font-medium">
                        {usage?.checksIncluded
                          ? `${Math.round(
                              ((usage?.checksUsed || 0) / usage.checksIncluded) *
                                100
                            )}%`
                          : "N/A"}
                      </span>
                    </div>
                    <Progress
                      value={
                        usage?.checksIncluded
                          ? ((usage?.checksUsed || 0) / usage.checksIncluded) * 100
                          : 0
                      }
                      className="h-3"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-foreground">Current User</h4>
                <p className="text-muted-foreground">{user?.email}</p>
                <p className="text-sm text-muted-foreground">Role: User</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground">
                  Account Status
                </h4>
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                >
                  Active
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={() => navigate("/billing")}
            className="bg-primary hover:bg-primary/90"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Manage Plan
          </Button>
          <Button variant="outline" onClick={() => navigate("/")}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
